import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import FareListTwoColumns from '../../lists/FareListTwoColumns';
import FareItemMediumSize from '../../items/FareItemMediumSize';
import DestinationItem from '../../items/DestinationItem';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonFeaturedDestinations';
import Loading from '../../others/Loading';
import NoFeaturedPopularFaresAlert from '../../alerts/NoFeaturedPopularFaresAlert';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';

//-------------------------------------
// Every Mundo integration
//-------------------------------------
import { getFares } from '../../../_integrations/everymundo/featuredPopularDestinations';
import { getAirports } from '../../../_integrations/everymundo/airports';

import prePopulationDefaultProps from '../_commons/prepopulationDefaultProps/groupedRoutesAggregation';

import NoFaresBehavior from '../../others/NoFaresBehavior/NoFaresBehavior';

import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useFareItemProps, useDestinationItemProps } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFilters';
import { getResultsLength } from '../_commons/featuredDestination';
import HeaderContainer from '../../elements/HeaderContainer';
import SecondaryFilters from '../../controls/SecondaryFilters';
import { generateSecondaryFiltersSettings } from '../_commons/generateSecondaryFiltersSettings';

function FeaturedDestinationContainer(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    getAirports: getAirportsFn,
    getFares: getFaresFn,
    paginationSettings,
    translate,
    direction,
    faresTrasformationLogic,
    showFilters,
    initialTopFeaturedDestination,
    initialFeaturedDestination,
    initialDepartureAirports,
    fareItemOnClickAction,
    filters: { availableFilters, ...filters },
    fareList,
    destinationItem,
    otherPopularDestinationsLabel,
    otherPopularDestinationsValue,
    showSkeletonScreen,
    loading,
    showLoading,
    noResultsPlaceHolder,
    alert,
    showDisclaimer,
    mainContentSettings,
    disclaimerContentSettings,
    otherPopularDestinationsContent,
    featuredTopDestinationsContentSettings,
    featuredDestinationsContentSettings,
    disclaimer: disclaimerSettings,
    hideBudgetOnMultiCurrency,
    skeletonSettings,
    noFaresBehavior,
    theme,
    // deprecated props
    filterContentSettings, // use filters.containerSettings
    locationSelector, // use filters.originDestinationSettings
    budgetSetting, // use filters.budgetSettings
    budgetSettingContainer, // use filters.budgetColumnSettings
    datesRangeSelector, // use filters.dateRangeSettings
    datesRangeSelectorContainer, // use filters.dateRangeColumnSettings
    clearAll, // use filters.clearAllSettings
    originDestinationLogic,
    secondaryFilters,
    showSecondaryFilters = false,
    enableSEO = true
  } = props;
  const [className, style] = useStyles(props, { direction, theme });

  const skeleton = <Skeleton {...skeletonSettings} position="absolute" width="100" />;

  const disclaimer = (
    <Disclaimer labelKey="cmp-featured-destination-container-disclaimer" {...disclaimerSettings} />
  );

  // Define filter props using deprecated container props (for backwards compatibility)
  // Don't rely on this props for new configurations, they will be removed in a future version
  const filtersConfig = {
    containerSettings: filterContentSettings,
    originDestinationSettings: {
      components: {
        origin: locationSelector,
        destination: locationSelector
      }
    },
    budgetSettings: budgetSetting,
    budgetColumnSettings: budgetSettingContainer,
    dateRangeSettings: datesRangeSelector,
    dateRangeColumnSettings: datesRangeSelectorContainer,
    clearAllSettings: clearAll,
    ...filters
  };

  const myStyle = Object.assign({ minHeight: '600px' }, style);

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports: getAirportsFn },
    initialData:
      initialFeaturedDestination || initialTopFeaturedDestination
        ? {
            topPopularDestination: initialTopFeaturedDestination,
            popularDestinations: initialFeaturedDestination
          }
        : undefined,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    getResultsLength,
    initialOrigins: initialDepartureAirports,
    getFares: getFaresFn,
    paginationSettings,
    faresTransformationLogic: faresTrasformationLogic,
    availableFilters: showFilters ? availableFilters : [],
    hideBudgetOnMultiCurrency,
    showOrigin:
      (filtersConfig.originDestinationSettings &&
        filtersConfig.originDestinationSettings.showOrigin) ||
      secondaryFilters?.originDestinationSettings?.showOrigin,
    showDestination:
      (filtersConfig.originDestinationSettings &&
        filtersConfig.originDestinationSettings.showDestination) ||
      secondaryFilters?.originDestinationSettings?.showDestination,
    showSecondaryFilters,
    availableSecondaryFilter: secondaryFilters ? secondaryFilters.availableFilters : []
  });

  const locationName = fares.loadedOrigin.label;
  const topPopularDestination = fares.data && fares.data.topPopularDestination;
  const popularDestinations = fares.data && (fares.data.popularDestinations || []);
  const mainContent = {
    style: {
      height: 500
    },
    ...mainContentSettings
  };

  let newDestinationSettings = {
    ...destinationItem
  };

  //INFO: https://trello.com/c/LSTHffTK
  if (popularDestinations && popularDestinations.length === 0) {
    newDestinationSettings.imageProps = {
      ...newDestinationSettings.imageProps,
      imageResizingParams: {
        worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
        height: 720,
        width: 1280,
        quality: 90,
        fit: 'crop',
        format: 'auto',
        opt: true
      },
      ...newDestinationSettings.imagePropsOneItem
    };
  }

  const itemSettings = useFareItemProps(fareList.itemSettings, dispatch, fareItemOnClickAction);

  let newItemsSettings = { ...itemSettings };
  let newSecondaryFilters = { ...secondaryFilters };
  let newFilters = { ...filtersConfig };

  if (
    filtersState.redemptionUnit.value === 'POINTS' ||
    filtersState.redemptionUnit.value === 'MILES'
  ) {
    newItemsSettings = {
      ...newItemsSettings,
      showPrice: false,
      showMiles: true,
      fareItemMiles: {
        ...newItemsSettings?.fareItemMiles,
        milesSymbol: filtersState.redemptionUnit.value.toLowerCase()
      }
    };
    newDestinationSettings = {
      ...newDestinationSettings,
      fareListTwoColumns: {
        ...newDestinationSettings?.fareListTwoColumns,
        itemSettings: {
          ...newDestinationSettings?.fareListTwoColumns?.itemSettings,
          showPrice: false,
          showMiles: true,
          fareItemMiles: {
            ...newDestinationSettings?.fareListTwoColumns?.itemSettings?.fareItemMiles,
            milesSymbol: filtersState.redemptionUnit.value.toLowerCase()
          }
        }
      }
    };
    newSecondaryFilters = generateSecondaryFiltersSettings(
      filtersState.redemptionUnit.value,
      newSecondaryFilters
    );
    newFilters = {
      ...newFilters,
      budgetSettings: {
        ...newFilters?.budgetSettings,
        currencyToDisplay: filtersState.redemptionUnit.value
      }
    };
  } else {
    newItemsSettings = {
      ...newItemsSettings,
      showMiles: false,
      showPrice: true
    };
    newDestinationSettings = {
      ...newDestinationSettings,
      fareListTwoColumns: {
        ...newDestinationSettings?.fareListTwoColumns,
        itemSettings: {
          ...newDestinationSettings?.fareListTwoColumns?.itemSettings,
          showPrice: true,
          showMiles: false
        }
      }
    };
  }

  const settingsDestinationItem = useDestinationItemProps(
    newDestinationSettings,
    dispatch,
    fareItemOnClickAction
  );

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  return (
    <Box
      position="relative"
      className={`${FeaturedDestinationContainer.componentName} ${className}`}
      style={myStyle}
      dataTestId="featuredDestinationContainer"
    >
      {showLoading && match('LOADING') && <Loading {...loading} />}
      {showSkeletonScreen && match('SKELETON') && skeleton}
      <HeaderContainer
        defaultTitleValue={'TOP TRAVEL DESTINATIONS'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
      {showFilters && <InlineFilters {...newFilters} {...filtersState} />}
      {showSecondaryFilters && (
        <SecondaryFilters
          {...filtersConfig}
          {...filtersState}
          {...newSecondaryFilters}
          filter={{ ...{ prePopulation, integrationSettings, meta, getAirports } }}
        />
      )}
      {
        <Row {...mainContent} gutters>
          {match('ERROR') && (
            <Col>
              <ContainerErrorAlert {...alert} tryAgain={retry} />
            </Col>
          )}
          {match('NO_RESULTS') && (
            <Col>
              <NoFeaturedPopularFaresAlert {...noResultsPlaceHolder} />
            </Col>
          )}
          {match('COMPLETE') && (
            <>
              {topPopularDestination && (
                <Col
                  display="flex"
                  flexDirection="column"
                  {...featuredTopDestinationsContentSettings}
                >
                  <DestinationItem
                    display="flex"
                    flexDirection="column"
                    flexGrow="1"
                    layout="fluid"
                    {...settingsDestinationItem}
                    showCityName={false}
                    image={topPopularDestination.destinationImage}
                    fares={[topPopularDestination]}
                  />
                </Col>
              )}
              {popularDestinations.length !== 0 && (
                <Col
                  lg="4"
                  display="flex"
                  flexDirection="column"
                  height="100"
                  {...featuredDestinationsContentSettings}
                >
                  <Box
                    marginBottom="2"
                    textAlign="left"
                    style={{ lineHeight: '1' }}
                    {...otherPopularDestinationsContent}
                  >
                    <Text {...otherPopularDestinationsLabel} type="span">
                      {translate(
                        `cmp-featured-destination-container-other-locations${
                          locationName ? '-from' : ''
                        }`,
                        'Other popular destinations'
                      )}
                    </Text>
                    {locationName && (
                      <Text {...otherPopularDestinationsValue} type="span">
                        {locationName}
                      </Text>
                    )}
                  </Box>
                  <FareListTwoColumns
                    {...fareList}
                    itemSettings={newItemsSettings}
                    ItemComponentInstance={FareItemMediumSize}
                    itemsData={popularDestinations}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
      }
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>{disclaimer}</Col>
        </Row>
      )}
    </Box>
  );
}

FeaturedDestinationContainer.propTypes = {
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to budget
   */
  budgetSetting: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to budget container
   */
  budgetSettingContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Props of clearAll
   */
  clearAll: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to destination item
   */
  destinationItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to dates range selector
   */
  datesRangeSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to dates range selectorm container
   */
  datesRangeSelectorContainer: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Possible ways to get fares from API
   */
  faresTrasformationLogic: PropTypes.oneOf(['grouped-routes', 'agreggation']),
  /**
   * Props to fare list
   */
  fareList: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * filterContentSettings props
   */
  filterContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * featuredTopDestinationsContentSettings props
   */
  featuredTopDestinationsContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * featuredDestinationsContentSettings props
   */
  featuredDestinationsContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to getFares
   */
  getFares: PropTypes.func,
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Used to prepopulate the component
   */
  initialDepartureAirports: PropTypes.arrayOf(PropTypes.any),
  /**
   * Used to prepopulate the component
   */
  initialFeaturedDestination: PropTypes.arrayOf(PropTypes.any),
  /**
   * Used to prepopulate the component with top popular
   */
  initialTopFeaturedDestination: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to location selector
   */
  locationSelector: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * Props of noResultsPlaceHolder
   */
  noResultsPlaceHolder: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other variable content
   */
  otherPopularDestinationsContent: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other fixed title
   */
  otherPopularDestinationsLabel: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to other variable title
   */
  otherPopularDestinationsValue: PropTypes.objectOf(PropTypes.any),
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Settings of the Pagination
   */
  paginationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Component PrePopulation
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Airport Iata Code in locations
   */
  showAirportIataCode: PropTypes.bool,
  /**
   * Determine if the component will show the disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Shows Loading Component
   */
  showLoading: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Determine if the component will show the filters.
   */
  showFilters: PropTypes.bool,
  /**
   * Hides budget filter when multiples currencies are allowed.
   * Defaults to true.
   */
  hideBudgetOnMultiCurrency: PropTypes.bool,
  /**
   * Determine if the component will show the title
   */
  showTitle: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Title Key to translate
   */
  titleKey: PropTypes.string,
  /**
   * Title Component
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * Props to translate
   */
  translate: PropTypes.func,
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Show Secondary Filters
   */
  showSecondaryFilters: PropTypes.bool,
  /**
   * Secondary Filters settings
   */
  secondaryFilters: PropTypes.objectOf(PropTypes.any)
};

FeaturedDestinationContainer.defaultProps = {
  showSecondaryFilters: false,
  secondaryFilters: {},
  alert: {
    bgColor: 'light',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  budgetSetting: {},
  budgetSettingContainer: {
    paddingTop: '2',
    className: 'fr-pt-lg-0'
  },
  containerType: 'stateful',
  clearAll: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    showIcon: true,
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  destinationItem: {},
  direction: 'ltr',
  disclaimer: {},
  disclaimerContentSettings: {
    marginTop: '2'
  },
  datesRangeSelector: {},
  datesRangeSelectorContainer: {
    paddingTop: '2',
    className: 'fr-pt-lg-0'
  },
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  faresTrasformationLogic: 'grouped-routes',
  featuredTopDestinationsContentSettings: {},
  featuredDestinationsContentSettings: {},
  fareList: {},
  filters: {
    availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget']
  },
  filterContentSettings: {},
  getFares,
  getAirports,
  initialDepartureAirports: null,
  initialFeaturedDestination: null,
  initialTopFeaturedDestination: null,
  loading: {},
  locationSelector: {},
  mainContentSettings: {
    marginTop: '3'
  },
  noFaresBehavior: 'hidden',
  noResultsPlaceHolder: {
    bgColor: 'light',
    rounded: '@theme:card.rounded'
  },
  otherPopularDestinationsContent: {},
  otherPopularDestinationsLabel: {},
  otherPopularDestinationsValue: {},
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  paginationSettings: {
    initialVisibleItems: 10
  },
  prePopulation: prePopulationDefaultProps,
  showAirportIataCode: true,
  showDisclaimer: true,
  showLoading: true,
  showSkeletonScreen: true,
  showFilters: true,
  showTitle: true,
  hideBudgetOnMultiCurrency: true,
  skeletonSettings: {
    style: { backgroundColor: 'white' }
  },
  titleKey: 'cmp-featured-destination-container-title',
  titleSettings: {
    margin: '0',
    textAlign: 'left'
  },
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListFeaturedDestination'
  },
  translate: () => {},
  localizationData: null,
  theme: null,
  integrationSettings: {},
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-feature-destination-sub-title',
  subTitleSettings: {}
};
FeaturedDestinationContainer.componentName = 'FeaturedDestinationContainer';

// HOC with common functionality to all containers
export default withCommonEMContainer(FeaturedDestinationContainer);
